import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  LayoutDashboard, Receipt, CreditCard, Plus, MoreHorizontal,
  Wallet, TrendingUp, FileText, Target, RepeatIcon, CheckSquare,
  Bot, Settings, UserCog, LogOut, Sun, Moon, X, CalendarDays, ArrowLeftRight, Gauge
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { resolveDashboardCards } from '../utils/dashboardCards';
import LancamentoModal from './LancamentoModal';
import ProfileModal from './ProfileModal';
import calendarGastifyIcon from '../assets/calendar-gastify.png';

const CalendarGastifyMoreIcon = ({ size = 20, className = '' }) => (
  <img
    src={calendarGastifyIcon}
    alt="Recorrências"
    className={`object-contain flex-shrink-0 ${className}`}
    style={{ width: size, height: size }}
  />
);

export default function BottomNav() {
  const { tenantSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const [novoModalOpen, setNovoModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  const isActive = (path) => location.pathname === `/${tenantSlug}/${path}`;

  const navButtonClass = (active) =>
    `flex flex-col items-center justify-center flex-1 py-1.5 px-1 transition-all duration-150 select-none ${
      active
        ? 'text-blue-600 dark:text-blue-400 font-bold scale-105'
        : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
    }`;

  const prefs = resolveDashboardCards(user);
  const moreItems = [
    { label: 'Carteira', path: 'carteira', icon: Wallet, color: 'text-blue-500 bg-blue-50 dark:bg-blue-950/50' },
    { label: 'Cartões', path: 'cartoes', icon: CreditCard, color: 'text-indigo-500 bg-indigo-50 dark:bg-indigo-950/50' },
    { label: 'Fluxo Caixa', path: 'fluxo-caixa', icon: TrendingUp, color: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-950/50' },
    { label: 'Calendário', path: 'calendario', icon: CalendarDays, color: 'text-indigo-500 bg-indigo-50 dark:bg-indigo-950/50' },
    { label: 'Importar/Exportar', path: 'importar-exportar', icon: ArrowLeftRight, color: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-950/50' },
    { label: 'Desempenho', path: 'desempenho', icon: Gauge, color: 'text-violet-500 bg-violet-50 dark:bg-violet-950/50' },
    { label: 'Relatórios', path: 'relatorios', icon: FileText, color: 'text-indigo-500 bg-indigo-50 dark:bg-indigo-950/50' },
    { label: 'Metas', path: 'metas', icon: Target, color: 'text-amber-500 bg-amber-50 dark:bg-amber-950/50' },
    { label: 'Recorrências', path: 'recorrencias', icon: CalendarGastifyMoreIcon, color: 'text-teal-500 bg-teal-50 dark:bg-teal-950/50' },
    { label: 'Tarefas', path: 'tarefas', icon: CheckSquare, color: 'text-teal-500 bg-teal-50 dark:bg-teal-950/50' },
    { label: 'Assistente IA', path: 'chat', icon: Bot, color: 'text-sky-500 bg-sky-50 dark:bg-sky-950/50' },
    { label: 'Configurações', path: 'configuracoes', icon: Settings, color: 'text-slate-500 bg-slate-100 dark:bg-slate-800' },
  ].filter(item => item.path !== 'fluxo-caixa' || prefs.menu_fluxo_caixa);

  const handleNavigate = (path) => {
    setMoreSheetOpen(false);
    navigate(`/${tenantSlug}/${path}`);
  };

  const initial = (user?.nomeCompleto || user?.username || 'A')[0].toUpperCase();

  return (
    <>
      {/* ─── BARRA DE NAVEGAÇÃO INFERIOR MOBILE (PWA / SMARTPHONE) ─── */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-t border-slate-200/80 dark:border-slate-800/80 shadow-[0_-8px_30px_rgba(0,0,0,0.08)] px-2 safe-bottom w-full max-w-full overflow-visible"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 8px)' }}
        data-testid="mobile-bottom-nav"
      >
        <div className="flex items-center justify-around h-16 w-full max-w-md mx-auto relative min-w-0">
          {/* 1. Início / Dashboard */}
          <NavLink
            to={`/${tenantSlug}/dashboard`}
            className={navButtonClass(isActive('dashboard'))}
            data-testid="bottom-nav-dashboard"
          >
            <LayoutDashboard size={20} className="mb-0.5" />
            <span className="text-[10px] tracking-tight">Início</span>
          </NavLink>

          {/* 2. Lançamentos */}
          <NavLink
            to={`/${tenantSlug}/lancamentos`}
            className={navButtonClass(isActive('lancamentos'))}
            data-testid="bottom-nav-lancamentos"
          >
            <Receipt size={20} className="mb-0.5" />
            <span className="text-[10px] tracking-tight">Lançamentos</span>
          </NavLink>

          {/* 3. BOTÃO CENTRAL FLUTUANTE "+ NOVO" */}
          <div className="flex flex-col items-center justify-center flex-1 -mt-4">
            <button
              onClick={() => setNovoModalOpen(true)}
              className="w-13 h-13 rounded-full bg-gradient-to-tr from-slate-900 to-slate-800 dark:from-blue-600 dark:to-indigo-600 text-white flex items-center justify-center shadow-[0_8px_20px_rgba(0,0,0,0.25)] border-4 border-slate-50 dark:border-slate-950 active:scale-95 transition-all cursor-pointer"
              aria-label="Novo Lançamento"
              data-testid="bottom-nav-novo-btn"
            >
              <Plus size={24} strokeWidth={2.5} />
            </button>
            <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300 mt-1">Novo</span>
          </div>

          {/* 4. Contas a Pagar */}
          <NavLink
            to={`/${tenantSlug}/contas-pagar`}
            className={navButtonClass(isActive('contas-pagar'))}
            data-testid="bottom-nav-contas"
          >
            <CreditCard size={20} className="mb-0.5" />
            <span className="text-[10px] tracking-tight">Contas</span>
          </NavLink>

          {/* 5. Mais / Gaveta de Módulos */}
          <button
            onClick={() => setMoreSheetOpen(true)}
            className={navButtonClass(moreSheetOpen)}
            data-testid="bottom-nav-more"
          >
            <MoreHorizontal size={20} className="mb-0.5" />
            <span className="text-[10px] tracking-tight">Mais</span>
          </button>
        </div>
      </nav>

      {/* ─── BOTTOM SHEET "MAIS MÓDULOS" (ESTILO NATIVO MOBILE) ─── */}
      {moreSheetOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end animate-in fade-in duration-200">
          {/* Backdrop escurecido */}
          <div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setMoreSheetOpen(false)}
          />

          {/* Painel que sobe da base da tela */}
          <div
            className="relative bg-white dark:bg-slate-900 rounded-t-[32px] border-t border-slate-200/80 dark:border-slate-800/80 shadow-2xl p-5 pt-3 z-10 max-h-[85vh] overflow-y-auto animate-in slide-in-from-bottom duration-200 safe-bottom"
            data-testid="mobile-more-sheet"
          >
            {/* Pílula de arrasto decorativa */}
            <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-700 rounded-full mx-auto mb-4" />

            {/* Cabeçalho da Gaveta com Usuário */}
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-3">
                {user?.fotoPerfil ? (
                  <img src={user.fotoPerfil} alt="" className="w-10 h-10 rounded-full object-cover border border-slate-200 dark:border-slate-700" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-slate-800 dark:bg-slate-700 text-white flex items-center justify-center font-bold text-sm uppercase">
                    {initial}
                  </div>
                )}
                <div>
                  <p className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-tight">
                    {user?.nomeCompleto || user?.username || 'Administrador'}
                  </p>
                  <p className="text-xs text-slate-400">@{user?.username || 'admin'}</p>
                </div>
              </div>
              <button
                onClick={() => setMoreSheetOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              >
                <X size={18} />
              </button>
            </div>

            {/* Grade de Módulos (Grid 4 colunas compacto) */}
            <div className="grid grid-cols-4 gap-3 mb-5">
              {moreItems.map(item => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavigate(item.path)}
                    className="flex flex-col items-center justify-center p-2 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800/60 active:scale-95 transition-all text-center group cursor-pointer"
                  >
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-1.5 transition-transform group-hover:scale-105 shadow-xs ${item.color}`}>
                      <Icon size={20} />
                    </div>
                    <span className={`text-[11px] font-semibold leading-tight line-clamp-1 ${active ? 'text-blue-600 dark:text-blue-400 font-bold' : 'text-slate-700 dark:text-slate-300'}`}>
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Ações de Conta no Rodapé da Gaveta */}
            <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
              <button
                onClick={() => {
                  setMoreSheetOpen(false);
                  setProfileModalOpen(true);
                }}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <UserCog size={18} className="text-slate-400" />
                  <span className="text-xs font-semibold">Meu Perfil & Senha</span>
                </div>
                <span className="text-[10px] text-slate-400 uppercase font-medium">Editar</span>
              </button>

              <button
                onClick={toggleTheme}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {theme === 'dark' ? <Moon size={18} className="text-indigo-400" /> : <Sun size={18} className="text-amber-500" />}
                  <span className="text-xs font-semibold">Tema {theme === 'dark' ? 'Escuro' : 'Claro'}</span>
                </div>
                <span className="text-[10px] text-slate-400 uppercase font-medium">Alternar</span>
              </button>

              <button
                onClick={() => {
                  setMoreSheetOpen(false);
                  logout();
                  navigate(`/${tenantSlug}/login`);
                }}
                className="w-full flex items-center justify-center gap-2 p-3 rounded-xl text-rose-600 bg-rose-50 dark:bg-rose-950/30 hover:bg-rose-100 dark:hover:bg-rose-950/50 font-semibold text-xs transition-colors"
              >
                <LogOut size={16} />
                <span>Sair da Conta</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Novo Lançamento acionado pelo botão central */}
      <LancamentoModal
        isOpen={novoModalOpen}
        onClose={() => setNovoModalOpen(false)}
        onSuccess={() => {
          setNovoModalOpen(false);
          // Se estiver na tela de lançamentos ou dashboard, atualiza a tela
          window.location.reload();
        }}
      />

      {/* Modal de Perfil acionado pela gaveta "Mais" */}
      <ProfileModal
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        kind="tenant"
        currentUser={user}
        onSuccess={refreshUser}
      />
    </>
  );
}
